import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import API_BASE_URL from './utils/config';

export const AuthContext = createContext(null);

function setAuthHeader(token) {
  if (token) axios.defaults.headers.common.Authorization = `Bearer ${token}`;
  else delete axios.defaults.headers.common.Authorization;
}

// Light JWT decode (no verification; fine for fallback display)
function decodeJWT(token) {
  try {
    const base64 = token.split('.')[1];
    const json = atob(base64.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decodeURIComponent(escape(json)));
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [hydrated, setHydrated] = useState(false);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [offline, setOffline] = useState(false); // for UI banner if you want

  const readStored = () => {
    const ls = localStorage.getItem('token');
    const us = localStorage.getItem('user'); // cached user JSON (string)
    if (ls) return { token: ls, cachedUser: us ? JSON.parse(us) : null, store: 'local' };
    const ss = sessionStorage.getItem('token');
    const uss = sessionStorage.getItem('user');
    if (ss) return { token: ss, cachedUser: uss ? JSON.parse(uss) : null, store: 'session' };
    return { token: null, cachedUser: null, store: null };
  };

  const writeStored = (tkn, usr, remember) => {
    // persist both token and user for offline fallback
    if (remember) {
      localStorage.setItem('token', tkn);
      localStorage.setItem('user', JSON.stringify(usr ?? {}));
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('user');
    } else {
      sessionStorage.setItem('token', tkn);
      sessionStorage.setItem('user', JSON.stringify(usr ?? {}));
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
  };

  const clearStored = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
  };

  // -------- Hydrate on first load --------
  useEffect(() => {
    (async () => {
      const { token: stored, cachedUser } = readStored();

      if (!stored) {
        // no token at all -> not logged in
        setHydrated(true);
        return;
      }

      // optimistic: set header & provisional user (from cache or decoded jwt)
      setAuthHeader(stored);
      setToken(stored);

      const decoded = decodeJWT(stored);
      const fallbackUser =
        cachedUser ||
        (decoded
          ? {
              id: decoded.sub || decoded.id,
              role: decoded.role,
              name: decoded.name,
              email: decoded.email,
            }
          : null);

      // Try validating token / fetching latest user
      try {
        const { data } = await axios.get(`${API_BASE_URL}/api/me`, { timeout: 5000 });
        setUser(data);
        setOffline(false);
        // refresh cache with authoritative user
        writeStored(stored, data, !!localStorage.getItem('token'));
      } catch (err) {
        // If backend explicitly says 401 -> token bad -> log out
        if (err?.response?.status === 401) {
          clearStored();
          setAuthHeader(null);
          setUser(null);
          setToken(null);
          setOffline(false);
        } else {
          // Network error / backend down / 5xx -> stay logged in **optimistically**
          setUser(fallbackUser);
          setOffline(true);
          // keep token & cached user so app continues
        }
      } finally {
        setHydrated(true);
      }
    })();
  }, []);

  // -------- Public API --------
  const login = (userData, jwtToken, remember = true) => {
    setUser(userData);
    setToken(jwtToken);
    setAuthHeader(jwtToken);
    writeStored(jwtToken, userData, remember);
    setOffline(false);
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    setAuthHeader(null);
    clearStored();
    setOffline(false);
  };

  const value = useMemo(
    () => ({ hydrated, user, token, offline, login, logout }),
    [hydrated, user, token, offline]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
